import { Box, Button, Checkbox, Input, TopBar, Wysiwyg } from '@sam/components';
import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Project, WorkInstruction } from 'shared/src/project/Project.types';
import { generateNewWorkInstruction } from '../../utils/project/Project.utils';
import { useUser } from '../../components/UserContext';
import { updateProject } from 'shared/src/project/Project.axios';
import { DocumentBucket, Offer, OfferType, Order, updateOrderAndOffer } from 'shared';
import { FileOverview } from '../../components/files/FileOverview';

export const WorkInstructionEdit: React.FC = () => {
  const { axios } = useUser();
  const location = useLocation<{
    project: Project;
    instruction?: WorkInstruction;
    order?: Order;
    offer?: Offer;
  }>();
  const [instruction, setInstruction] = useState<WorkInstruction>(
    location.state?.instruction ||
      generateNewWorkInstruction({
        projectId: location.state?.project?.id || '',
        index: (location.state?.project?.instructions.length || 0) + 1,
      }),
  );
  const { t } = useTranslation();
  const navigate = useNavigate();

  /**
   * Helper method to update an existing instruction or add a new one
   * @param evt FormEvent to prevent default behaviour
   */
  const handleSubmit = (evt: FormEvent): void => {
    evt.preventDefault();
    const passedOffer: Offer | undefined = location.state?.offer;
    const passedOrder: Order | undefined = location.state?.order;
    const passedProject: Project | undefined = location.state?.project;
    const passedInstruction: WorkInstruction | undefined = location.state?.instruction;
    if (
      passedOffer?.offerType === OfferType.CUSTOMER_PROJECT &&
      passedOrder &&
      passedOffer.workInstructions
    ) {
      const updatedInstructions: WorkInstruction[] = passedInstruction
        ? passedOffer.workInstructions?.map((prevInstruction) =>
            prevInstruction.id === instruction.id ? instruction : prevInstruction,
          )
        : [...passedOffer.workInstructions, instruction];
      const updatedOffer: Offer = { ...passedOffer, workInstructions: updatedInstructions };
      updateOrderAndOffer(axios, updatedOffer, passedOrder).then(
        (updated) =>
          updated &&
          navigate('/customer/project/edit', {
            state: { offer: updated.offer, order: updated.order },
          }),
      );
    }
    //If an instruction was passed we want to edit it
    else if (passedInstruction && passedProject)
      updateProject(axios, {
        ...passedProject,
        instructions: passedProject.instructions.map((existingInstruction) =>
          existingInstruction.id === instruction.id ? instruction : existingInstruction,
        ),
      }).then((updatedProject) => {
        if (updatedProject)
          navigate('/project/edit', {
            state: { project: updatedProject, order: location.state?.order },
          });
      });
    //if no instruction was passed we add a new one
    else if (passedProject)
      updateProject(axios, {
        ...passedProject,
        instructions: [...passedProject.instructions, instruction],
      }).then((updatedProject) => {
        if (updatedProject && location.state?.order)
          navigate('/project/edit', {
            state: {
              project: updatedProject,
              order: location.state?.order,
            },
          });
      });
  };

  /**
   * Handler to delete an instruction
   */
  const handleDeleteInstruction = (): void => {
    if (location.state?.offer?.workInstructions && location.state.order) {
      updateOrderAndOffer(
        axios,
        {
          ...location.state.offer,
          workInstructions: location.state.offer.workInstructions.filter(
            (filterInstruction) => filterInstruction.id !== instruction.id,
          ),
        },
        location.state.order,
      ).then(
        (updated) =>
          updated &&
          navigate('/customer/project/edit', {
            state: { offer: updated.offer, order: updated.order },
          }),
      );
    } else if (location.state?.project)
      updateProject(axios, {
        ...location.state.project,
        instructions: location.state.project.instructions.filter(
          (filterInstruction) => filterInstruction.id !== instruction.id,
        ),
      }).then(
        (updatedProject) =>
          updatedProject &&
          navigate('/project/edit', {
            state: {
              project: updatedProject,
              order: location.state?.order,
            },
          }),
      );
  };

  return (
    <form onSubmit={handleSubmit}>
      <TopBar
        onBackClick={() => navigate(-1)}
        title={
          location.state?.instruction
            ? t('pages.workInstructions.topBarHeadlineEdit')
            : t('pages.workInstructions.topBarHeadlineCreate')
        }
      >
        {location.state?.instruction && (
          <Button
            value={t('general.buttons.delete')}
            onClick={handleDeleteInstruction}
            backgroundColor="#bc2e46"
          />
        )}
        <Button value={t('general.buttons.save')} type="submit" />
      </TopBar>
      <Box title={t('pages.workInstructions.content')}>
        <Wysiwyg
          initialContent={instruction.content}
          onChange={(content) => setInstruction({ ...instruction, content })}
        />
      </Box>
      {location.state?.instruction && (
        <>
          <Box title={t('pages.workInstructions.title')}>
            <Checkbox
              isChecked={!instruction.disabled}
              label={t('pages.workInstructions.active')}
              onCheck={(checked) => setInstruction({ ...instruction, disabled: !checked })}
            />
            <div className="three-columns">
              <Input
                label={t('pages.workInstructions.instructionAt')}
                value={instruction.instructionTimeStamp || new Date()}
                type="date"
                onChangeDate={(instructionTimeStamp) =>
                  instructionTimeStamp && setInstruction({ ...instruction, instructionTimeStamp })
                }
              />
              <Input
                value={instruction.instructionBy || ''}
                type="text"
                label={t('pages.workInstructions.instructionBy')}
                onChange={(instructionBy) => setInstruction({ ...instruction, instructionBy })}
              />
              <Input
                value={instruction.instructionFrom || ''}
                type="text"
                label={t('pages.workInstructions.instructionFrom')}
                onChange={(instructionFrom) => setInstruction({ ...instruction, instructionFrom })}
              />
            </div>
          </Box>
          <Box>
            <FileOverview
              instructionId={instruction.id}
              bucket={DocumentBucket.INSTRUCTION}
              path={`${location.state.order?.customerId}/${location.state.order?.id}/instructions/${location.state.instruction.id}/`}
            />
          </Box>
        </>
      )}
    </form>
  );
};
